(function (scope) {
  'use strict'

  function installLazyWidgetLoader (window, options = {}) {
    if (window.shipaidCartLoader && window.shipaidCartLoader.status() !== 'DESTROYED') return window.shipaidCartLoader
    const document = window.document
    const root = '/' + String(options.root || '/').split('/').filter(Boolean).join('/')
    const cartPath = (root === '/' ? '' : root) + '/cart'
    let state = window.customElements?.get('shipaid-widget') ? 'READY' : 'IDLE'
    let pending
    let claimedRuntime
    const detach = []
    const registered = () => Boolean(window.customElements?.get('shipaid-widget'))
    const normalizedPath = value => {
      try {
        const url = new window.URL(value, window.location.href)
        return url.origin === window.location.origin ? url.pathname.replace(/\/$/, '') : ''
      } catch { return '' }
    }
    const isVisible = element => {
      for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
        const style = window.getComputedStyle(node)
        if (node.hidden || node.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || style.opacity === '0') return false
      }
      return true
    }
    const hasOpenCart = () => {
      const container = document.querySelector('#upCart')
      if (!container) return false
      const popup = container.querySelector('#UpcartPopup')
      // UpCart keeps its closed drawer in the DOM with display:block.
      return popup ? popup.classList.contains('upcartPopupShow') && isVisible(popup) : Boolean(container.querySelector('a[href*="/checkout"], .upcart-footer')) && isVisible(container)
    }
    const existingRuntime = () => {
      const shared = window.__shipaidStorefrontRuntimeV1
      return Array.from(document.querySelectorAll('script[src]')).find(script => {
        if (shared?.status === 'failed' && script.src === shared.runtimeUrl) return false
        if (['legacy', 'cart-cdn-candidate'].includes(script.getAttribute('data-shipaid-runtime'))) return true
        try {
          const url = new window.URL(script.src, window.location.href)
          return script.src === options.runtimeUrl || (url.origin === 'https://unpkg.com' && /^\/ui\.shipaid\.com(?:@[^/]+)?\/dist\/widget(?:\.(?:es|umd|iife))?\.js$/.test(url.pathname))
        } catch { return false }
      })
    }
    const waitForScript = script => new Promise((resolve, reject) => {
      if (registered()) { resolve(); return }
      const loaded = () => { cleanup(); registered() ? resolve() : reject(new Error('Core widget did not register')) }
      const failed = () => { cleanup(); reject(new Error('Widget runtime could not load')) }
      const cleanup = () => {
        script.removeEventListener('load', loaded)
        script.removeEventListener('error', failed)
      }
      script.addEventListener('load', loaded, { once: true })
      script.addEventListener('error', failed, { once: true })
    })
    const importRuntime = async reason => {
      if (typeof options.runtimeUrl !== 'string' || !options.runtimeUrl.trim()) throw new Error('A configured HTTPS CDN runtime is required')
      const url = new window.URL(options.runtimeUrl, window.location.href)
      if (url.protocol !== 'https:') throw new Error('A configured HTTPS CDN runtime is required')
      const script = document.createElement('script')
      script.type = 'module'
      script.src = url.href
      script.setAttribute('data-shipaid-runtime', 'cart-cdn-candidate')
      script.setAttribute('data-shipaid-load-reason', ['OPEN_CART', 'ADD_TO_CART', 'VISIBLE_CART', 'FULL_CART', 'RETRY'].includes(reason) ? reason : 'MANUAL')
      const shared = claimedRuntime || { protocol: 1, source: 'cart-cdn-candidate', runtimeUrl: url.href }
      shared.status = 'loading'
      window.__shipaidStorefrontRuntimeV1 = shared
      const completion = waitForScript(script)
      document.head.appendChild(script)
      try {
        await completion
        shared.status = 'loaded'
      } catch (error) {
        shared.status = 'failed'
        script.remove()
        throw error
      }
    }
    function start (reason) {
      if (state === 'DESTROYED' || state === 'READY') return Promise.resolve(state === 'READY')
      if (registered()) { state = 'READY'; retire(); return Promise.resolve(true) }
      if (pending) return pending
      state = 'LOADING'
      pending = Promise.resolve().then(() => {
        if (registered()) return
        const existing = existingRuntime()
        return existing ? waitForScript(existing) : (options.loadRuntime || importRuntime)(reason)
      }).then(() => {
        if (state !== 'DESTROYED') { state = 'READY'; retire() }
        return state === 'READY'
      }, error => {
        if (state !== 'DESTROYED') {
          state = 'ERROR'
          try { if (options.onError) options.onError(error) } catch {}
        }
        return false
      }).finally(() => { pending = undefined })
      return pending
    }
    const listen = (target, name, handler) => {
      target.addEventListener(name, handler, true)
      detach.push(() => target.removeEventListener(name, handler, true))
    }
    const click = event => {
      const path = event.composedPath ? event.composedPath() : [event.target]
      for (const node of path) {
        if (!node?.matches) continue
        if (node.matches('[data-action="toggle-cart"], [data-cart-toggle], [aria-controls="cart-drawer"], [aria-controls="CartPopup"]') || (node.matches('a[href]') && normalizedPath(node.href) === cartPath)) {
          start('OPEN_CART'); return
        }
      }
    }
    const submit = event => {
      if (event.target?.matches('form') && normalizedPath(event.target.action).replace(/\.js$/, '') === cartPath + '/add') start('ADD_TO_CART')
    }
    const inspect = () => {
      if (state === 'IDLE' && hasOpenCart()) start('VISIBLE_CART')
    }
    listen(document, 'click', click)
    listen(document, 'submit', submit)
    listen(document, 'upcart:open', () => start('OPEN_CART'))
    listen(window, 'upcart:open', () => start('OPEN_CART'))
    // Initial hydration can emit cart updates without any customer intent.
    listen(document, 'cart:updated', inspect)
    const visibilityObserver = new window.MutationObserver(inspect)
    let watched = []
    const watchVisibility = () => {
      const container = document.querySelector('#upCart')
      const nodes = []
      for (let node = container; node; node = node.parentElement) nodes.push(node)
      const popup = container?.querySelector('#UpcartPopup')
      if (popup) nodes.push(popup)
      if (nodes.length === watched.length && nodes.every((node, index) => node === watched[index])) return
      visibilityObserver.disconnect()
      watched = nodes
      nodes.forEach(node => visibilityObserver.observe(node, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] }))
    }
    const observer = new window.MutationObserver(() => { watchVisibility(); inspect() })
    observer.observe(document.documentElement, { childList: true, subtree: true })
    const retire = () => { observer.disconnect(); visibilityObserver.disconnect(); detach.splice(0).forEach(remove => remove()) }
    watchVisibility()
    const controller = {
      start,
      retry: () => start('RETRY'),
      status: () => state,
      destroy: () => {
        if (state === 'IDLE' && claimedRuntime && window.__shipaidStorefrontRuntimeV1 === claimedRuntime) delete window.__shipaidStorefrontRuntimeV1
        state = 'DESTROYED'; retire()
      }
    }
    window.shipaidCartLoader = controller
    if (state === 'IDLE' && window.__shipaidStorefrontRuntimeV1 == null && !existingRuntime()) {
      try {
        if (typeof options.runtimeUrl === 'string' && options.runtimeUrl.trim()) {
          const url = new window.URL(options.runtimeUrl, window.location.href)
          if (url.protocol === 'https:') {
            claimedRuntime = { protocol: 1, source: 'cart-cdn-candidate', status: 'deferred', runtimeUrl: url.href }
            // The verified legacy loader honors this shared protocol before appending its module.
            window.__shipaidStorefrontRuntimeV1 = claimedRuntime
          }
        }
      } catch {}
    }
    if (state === 'READY') retire()
    else if (state === 'IDLE' && normalizedPath(window.location.pathname) === cartPath) start('FULL_CART')
    else inspect()
    return controller
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { installLazyWidgetLoader }
  else if (scope.document?.currentScript?.dataset.shipaidRuntimeUrl) {
    const script = scope.document.currentScript
    installLazyWidgetLoader(scope, { runtimeUrl: script.dataset.shipaidRuntimeUrl, root: script.dataset.shopifyRoot || '/' })
  }
})(typeof window !== 'undefined' ? window : undefined)
